/**
 * Title + description copy for the TalentPop "scale your team"
 * notification.
 *
 * The notification fires from one of two triggers (mirroring the
 * triggers behind the support-v2 "Add agents" button):
 *
 *   - `volume`: a month-to-date ticket-volume surge.
 *   - `holiday`: the 21-day run-up to a major retail holiday.
 *
 * The copy varies; the description (TalentPop value prop) is the
 * same in both. Trigger resolution itself lives elsewhere — this
 * module is purely the copy lookup so it stays trivially testable.
 */

import type { HolidayKey } from "./holiday-trigger";

export type TalentPopTrigger =
  | { readonly type: "volume" }
  | { readonly type: "holiday"; readonly holidayKey: HolidayKey };

export interface TalentPopNotificationCopy {
  readonly title: string;
  readonly description: string;
}

/** Display label for each holiday key. Used inline in the holiday
 *  notification title ("{label} is coming up"). */
const HOLIDAY_LABEL: Record<HolidayKey, string> = {
  "valentines-day": "Valentine's Day",
  "mothers-day": "Mother's Day",
  "july-4th": "4th of July",
  "labor-day": "Labor Day",
  "black-friday": "Black Friday",
  christmas: "Christmas",
};

/** Shared TalentPop value prop — the description does not vary by
 *  trigger, only the headline does. */
const DESCRIPTION =
  "Scale your support team with TalentPop's trained CX agents, live in 2-3 weeks.";

export function getTalentPopNotificationCopy(
  trigger: TalentPopTrigger,
): TalentPopNotificationCopy {
  if (trigger.type === "holiday") {
    return {
      title: `${HOLIDAY_LABEL[trigger.holidayKey]} is coming up`,
      description: DESCRIPTION,
    };
  }
  return { title: "Ticket volume spike detected", description: DESCRIPTION };
}
